import _ from 'underscore';
import { useCallback, useState, useEffect, useContext, useMemo } from 'react';

import { ScopeContext } from '@bingads-webui-react/scope-service';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { ImageOData } from '@bingads-webui-campaign/image-odata';
import { getODataConfig } from '@bingads-webui-campaign-react/perf-max-recommendation';

import { getImagesFromMT, uploadSuggestedImages } from './components/image-experience-utils';
import { useAdAssetsInstrument } from './use-ad-assets-instrument';

const NoImagesErrorMessage = _TL_('You need to add at least 1 image with 1.91:1 aspect ratio and 1 image with 1:1 aspect ratio to your asset group.');

export const useImageListStateHandler = (i18n, data, onDataChange) => {
  const scopeContext = useContext(ScopeContext);
  const serverContext = useContext(ServerContext);
  const adAssetsActivity = useAdAssetsInstrument();

  const {
    aid, cid,
  } = _.get(scopeContext, ['scope', 'values'], {});

  const [imageList, setImageList] = useState([]);
  const [selectedImageId, setSelectedImageId] = useState(undefined);
  const [cropImageObj, setCropImageObj] = useState(null);
  const [editingPlacementType, setEditingPlacementType] = useState('');
  const [errorMessages, setErrorMessages] = useState([]);
  const [isDirty, setIsDirty] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isImageListInitialized, setIsImageListInitialized] = useState(false);

  const isImageListEmpty = _.isEmpty(imageList);

  const imageOdata = useMemo(() => new ImageOData(cid, aid, getODataConfig(serverContext)), [cid, aid, serverContext]);

  useEffect(() => {
    const pendingImages = _.get(data, ['assetGroupData', 'Images', 'PendingImages'], []);
    const suggestedImages = _.get(data, ['assetGroupData', 'Images', 'SuggestedImages'], []);

    if (!isImageListInitialized) {
      if (data && data.assetGroupData && _.isEmpty(pendingImages) && _.isEmpty(suggestedImages)) {
        setIsImageListInitialized(true);
      }

      if ((!_.isEmpty(pendingImages) || !_.isEmpty(suggestedImages)) && !isLoading) {
        setIsLoading(true);

        const loadImagesFn = !_.isEmpty(pendingImages)
          ? () => getImagesFromMT(pendingImages, imageOdata)
          : () => uploadSuggestedImages(suggestedImages, imageOdata, cid, aid);

        loadImagesFn()
          .then((loadedImageList) => {
            setImageList(loadedImageList);

            onDataChange({
              data: prevData => ({
                ...prevData,
                assetGroupData: {
                  ...prevData.assetGroupData,
                  Images: {
                    ImageList: loadedImageList,
                  },
                },
              }),
            });

            setSelectedImageId(_.get(loadedImageList, [0, 0, 'Id']));
          })
          .catch((error) => {
            adAssetsActivity.error(`load images error: ${JSON.stringify(error)}`);
          })
          .finally(() => {
            setIsImageListInitialized(true);
            setIsLoading(false);
          });
      }
    }
  }, [adAssetsActivity, aid, cid, data, imageOdata, isImageListInitialized, isLoading, onDataChange]);

  useEffect(() => {
    setErrorMessages(isImageListEmpty ? [i18n.getString(NoImagesErrorMessage)] : []);
  }, [i18n, isImageListEmpty, onDataChange]);

  const onClearList = useCallback(() => {
    setImageList([]);
  }, []);

  const onImageSelected = useCallback((imageId) => {
    setCropImageObj(null);
    setEditingPlacementType('');
    setSelectedImageId(imageId);
  }, []);

  const onImageListUpdated = useCallback((newImageList, newImageId) => {
    setImageList(newImageList);
    setIsDirty(true);
    setCropImageObj(null);
    setEditingPlacementType('');

    onDataChange({
      data: prevData => ({
        ...prevData,
        assetGroupData: {
          ...prevData.assetGroupData,
          Images: {
            ...prevData.assetGroupData.Images,
            ImageList: newImageList,
          },
        },
      }),
    });

    if (newImageId) {
      onImageSelected(newImageId);
    }
  }, [onDataChange, onImageSelected]);

  return {
    imageListState: {
      imageList,
      selectedImageId,
      errorMessages,
      isDirty,
      cropImageObj,
      editingPlacementType,
      isLoading,
      isImageListInitialized,
    },
    onClearList,
    onImageListUpdated,
    onImageSelected,
    onSetCropImageObj: setCropImageObj,
    onSetEditingPlacementType: setEditingPlacementType,
  };
};
